import {
  Button,
  ButtonVariants,
  FormattedMessage,
  IconButton,
  IconName,
  InlineFormattedNumber,
  Meter,
  OrdStatusEnum,
  OrderStatus,
  SecurityRenderer,
  SmartSummary,
  Text,
  WLCanResubmitCustomerOrder,
  WL_ACTION,
  abbreviateId,
  formattedDate,
  getIntlKey,
  getOrderStatusText,
  includesEnumValue,
  isKeyIn,
  logger,
  orderSliceMessages,
  orderStatusTextToColor,
  useGetStrategyLikeLabel,
  useOnClickOutside,
  useSecurity,
  useStrategiesContext,
  useWLCanCancelCustomerOrder,
  useWLCanModifyCustomerOrder,
  useWLRoleAuth,
  type CustomerOrder,
} from '@talos/kyoko';
import Big from 'big.js';
import { noop, sortBy } from 'lodash-es';
import { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { ACTIVE_ORDER_STATUSES } from '../../../tokens/order';
import { ExecutionCardWrapper, Footer, Header, Label, ParametersSection, Row, Section, Value } from './styles';

const messages = defineMessages({
  limitPrice: {
    defaultMessage: 'Limit Price',
    id: 'OrderForms.ExecutionCard.limitPrice',
  },
  strategy: {
    defaultMessage: 'Strategy',
    id: 'OrderForms.ExecutionCard.strategy',
  },
  quantity: {
    defaultMessage: 'Quantity',
    id: 'OrderForms.ExecutionCard.quantity',
  },
  averageFillPrice: {
    defaultMessage: 'Average Fill Price',
    id: 'OrderForms.ExecutionCard.averageFillPrice',
  },
  filledQty: {
    defaultMessage: 'Filled Quantity',
    id: 'OrderForms.ExecutionCard.filledQty',
  },
  cancelOrder: {
    defaultMessage: 'Cancel Order',
    id: 'OrderForms.ExecutionCard.cancelOrder',
  },
  modify: {
    defaultMessage: 'Modify',
    id: 'OrderForms.ExecutionCard.modify',
  },
  resubmit: {
    defaultMessage: 'Resubmit',
    id: 'OrderForms.ExecutionCard.resubmit',
  },
  archiveOrder: {
    defaultMessage: 'Archive',
    id: 'OrderForms.ExecutionCard.archiveOrder',
  },
  id: {
    defaultMessage: 'ID',
    id: 'OrderForms.ExecutionCard.id',
  },
});

export const ExecutionCard = ({
  order,
  attached,
  onCancelOrder,
  onCloseCard,
  onModifyOrder,
  onArchiveOrder,
  onResubmitOrder,
  showSubmitTime,
}: ExecutionCardProps) => {
  const {
    Currency,
    OrdStatus,
    OrderQty,
    CumQty,
    DecisionStatus,
    PricingMode,
    OrderID,
    Text: text,
    Price,
    Parameters,
    AvgPx,
    SubmitTime,
  } = order;
  const { isAuthorized } = useWLRoleAuth();

  const status = getOrderStatusText({
    type: 'orderLike',
    order,
    isCustomerOrder: true,
  });

  const { strategiesByName } = useStrategiesContext();
  const security = useSecurity(order.Symbol);
  const strategy = order?.Strategy ? strategiesByName.get(order.Strategy) : undefined;
  const wrapperRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(wrapperRef, onCloseCard != null ? onCloseCard : noop);
  const { QuoteCurrency, DefaultSizeIncrement, DefaultPriceIncrement } = security ?? {};

  const isCcy = Currency === QuoteCurrency;
  const qtyIncrement = isCcy ? DefaultPriceIncrement : DefaultSizeIncrement;

  const theme = useTheme();
  const { baseSize } = theme;

  const filledShare = Big(OrderQty ?? '0').gt(0) ? Big(CumQty ?? '0').div(OrderQty) : Big(0);

  const progressBarColor = orderStatusTextToColor(status, theme);

  const isActive = includesEnumValue(ACTIVE_ORDER_STATUSES, order.OrdStatus);

  const formattedParametersUnsorted = Object.entries(Parameters ?? {})
    .filter(([key]) => key !== 'ErrorAction')
    .map(([key, v]) => {
      if (typeof v !== 'string' && typeof v !== 'number') {
        return null;
      }
      if (strategy != null) {
        const index = strategy.Parameters.findIndex(param => param.Name === key);
        if (index === -1) {
          // If we can't find any param, something is wrong. Returning undefined makes nothing show.
          logger.error(new Error('Unsupported parameter found on Order'), {
            extra: {
              orderParameters: order.Parameters,
            },
          });
          return undefined;
        }

        const { DisplayName, Type } = strategy.Parameters[index];
        const label = DisplayName;
        const value = (() => {
          switch (Type) {
            case 'Date':
              return formattedDate(Sugar.Date.create(v));
            case 'Price':
              return (
                <InlineFormattedNumber
                  number={v as string}
                  increment={DefaultPriceIncrement}
                  currency={QuoteCurrency}
                />
              );
            case 'Qty':
              return <InlineFormattedNumber number={v as string} increment={qtyIncrement} currency={Currency} />;
            case 'Percent':
              return <InlineFormattedNumber number={Big(v).times(100).toFixed()} increment="0.01" currency="%" />;
            default:
              return v;
          }
        })();

        return { label, value };
      } else {
        return { label: key, value: v };
      }
    })
    .compact();

  const formattedParameters = sortBy(formattedParametersUnsorted, item => item.label);
  const intl = useIntl();

  const getStrategyLikeLabel = useGetStrategyLikeLabel();
  const canCancelCustomerOrder = useWLCanCancelCustomerOrder();
  const canModifyCustomerOrder = useWLCanModifyCustomerOrder();
  const isModificationAllowed = canModifyCustomerOrder(order);
  const isCancellationAllowed = canCancelCustomerOrder(order);

  return (
    <ExecutionCardWrapper ref={wrapperRef} attached={attached} status={status} data-testid="order-form-execution-card">
      {onCloseCard && <IconButton onClick={onCloseCard} icon={IconName.Close} ghost />}
      <Header justifyContent={showSubmitTime ? 'space-between' : 'center'}>
        {/* order.Symbol should always be defined in whitelabel, but type-guarding for safety anyway */}
        {order.Symbol && (
          <SecurityRenderer
            showDescription={false}
            security={security}
            symbol={order.Symbol}
            iconSize={baseSize * 1.5}
          />
        )}
        {showSubmitTime && (
          <Text
            data-testid="execution-card-submit-time"
            fontSize="fontSizeSmall"
            color="colorTextDefault"
            mr={onCloseCard === undefined ? undefined : 'spacing24'}
          >
            {formattedDate(SubmitTime)}
          </Text>
        )}
      </Header>
      <Section>
        <Row>
          <OrderStatus
            ordStatus={OrdStatus}
            decisionStatus={DecisionStatus}
            cumQty={CumQty}
            orderQty={OrderQty}
            iconPlacement="left"
            pricingMode={PricingMode}
            isCustomerOrder={true}
          />
          <Label>
            <FormattedMessage {...messages.id} />
          </Label>
          <Value>{abbreviateId(OrderID)}</Value>
        </Row>
      </Section>
      <Section>
        <Row>
          <Label>
            <FormattedMessage {...messages.quantity} />
          </Label>
          <Value>
            <SmartSummary
              entity={order}
              type="order"
              showSide
              showQtyCurrency
              security={security?.Symbol}
              showPrice={false}
            />
          </Value>
        </Row>
        {Price != null && (
          <Row>
            <Label>
              <FormattedMessage {...messages.limitPrice} />
            </Label>
            <Value>
              <InlineFormattedNumber number={Price} increment={DefaultPriceIncrement} currency={QuoteCurrency} />
            </Value>
          </Row>
        )}
        {strategy != null && (
          <Row>
            <Label>
              <FormattedMessage {...messages.strategy} />
            </Label>
            <Value>{getStrategyLikeLabel(strategy)}</Value>
          </Row>
        )}
        {strategy != null && (
          <ParametersSection>
            {formattedParameters.map(({ label, value }) => {
              const intlKey = getIntlKey(label);
              const translatedLabel = isKeyIn(intlKey, orderSliceMessages)
                ? intl.formatMessage(orderSliceMessages[intlKey])
                : label;
              return (
                <Row key={label}>
                  <Label>{translatedLabel}</Label>
                  <Value>{value}</Value>
                </Row>
              );
            })}
          </ParametersSection>
        )}
      </Section>
      {OrdStatus !== OrdStatusEnum.Rejected && (
        <Section>
          <Row>
            <Label>
              <FormattedMessage {...messages.averageFillPrice} />
            </Label>
            <Value>
              <InlineFormattedNumber number={AvgPx} increment={DefaultPriceIncrement} currency={QuoteCurrency} />
            </Value>
          </Row>
          <Row>
            <Label>
              <FormattedMessage {...messages.filledQty} />
            </Label>
            <Value>
              <InlineFormattedNumber
                number={filledShare.times(100).toFixed()}
                increment="0.01"
                currency="%"
                round={true}
              />{' '}
              / <InlineFormattedNumber number={CumQty} increment={qtyIncrement} currency={Currency} />
            </Value>
          </Row>
          <Row>
            <Meter value={filledShare.toNumber()} color={progressBarColor} showLabel={false} />
          </Row>
        </Section>
      )}
      <Section>
        <Text color="colorTextWarning">{text}</Text>
      </Section>
      <Footer>
        {isActive ? (
          <>
            {isAuthorized(WL_ACTION.CANCEL_ORDER) && onCancelOrder && (
              <Button
                data-testid="cancel-order"
                onClick={() => {
                  if (isCancellationAllowed) {
                    onCancelOrder(order);
                  }
                }}
                disabled={!isCancellationAllowed}
              >
                <FormattedMessage {...messages.cancelOrder} />
              </Button>
            )}
            {isAuthorized(WL_ACTION.MODIFY_ORDER) && onModifyOrder && (
              <Button
                data-testid="modify-order"
                onClick={() => {
                  if (isModificationAllowed) {
                    onModifyOrder(order);
                  }
                }}
                variant={ButtonVariants.Primary}
                disabled={!isModificationAllowed}
              >
                <FormattedMessage {...messages.modify} />
              </Button>
            )}
          </>
        ) : (
          <>
            {onArchiveOrder && (
              <Button onClick={() => onArchiveOrder(OrderID)} data-testid="archive-button">
                <FormattedMessage {...messages.archiveOrder} />
              </Button>
            )}
            {isAuthorized(WL_ACTION.INITIATE_ORDER) && onResubmitOrder && (
              <Button
                onClick={() => onResubmitOrder(order)}
                variant={ButtonVariants.Primary}
                disabled={!WLCanResubmitCustomerOrder(order)}
                data-testid="resubmit-button"
              >
                <FormattedMessage {...messages.resubmit} />
              </Button>
            )}
          </>
        )}
      </Footer>
    </ExecutionCardWrapper>
  );
};

type ExecutionCardProps = {
  order: CustomerOrder;
  attached: boolean;
  onCloseCard: (() => void) | undefined;
  onResubmitOrder: ((order: CustomerOrder) => void) | null;
  onModifyOrder: ((order: CustomerOrder) => void) | null;
  onCancelOrder: ((order: CustomerOrder) => void) | null;
  onArchiveOrder: ((orderId: string) => void) | null;
  showSubmitTime?: boolean;
};
